import {isAutoGeneratedTemplateFieldKey, isReservedAutoInjectedTemplateFieldKey, toTableColumnState} from './client.core.js';
import {
  DEFAULT_LAYOUT_CONTEXT,
  FIELD_TYPE,
  GRID_FIELD,
  gridFieldKey,
  PIN_POSITION_FIELD_KEY,
  SLUG_FIELD_KEY,
} from '../vocabulary.js';

/**
 * Editor state of one template field in the admin field list, keyed by field key. `manual` marks
 * a field added by hand rather than detected in the template content.
 * @typedef {Object} FieldConfig
 * @property {string} label
 * @property {import('../vocabulary.js').FieldType} type
 * @property {boolean} required
 * @property {boolean} manual
 * @property {string | null} [editorGroup]
 * @property {string | null} [help] Plain text shown under the field's label in the page editor
 * @property {string | number | null} [recommendedLength] Only on text/textarea fields: characters the page editor's live counter measures against
 * @property {string | null} [dependsOn]
 * @property {string} [options] Raw options textarea input, one option per line
 * @property {import('../../shell/types/client.types.js').TableColumnState[]} [columns]
 * @property {string | number} [maxRows]
 */

/**
 * @typedef {Object} PresetField
 * @property {string} key
 * @property {string} label
 * @property {import('../vocabulary.js').FieldType} type
 * @property {boolean} required
 */

/** Pattern a manually added field key must match. */
export const FIELD_KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

/**
 * Field sets the manual field picker can add in one go, keyed by the option values of its preset
 * select.
 * @type {Readonly<Record<string, PresetField[]>>}
 */
export const MANUAL_FIELD_PRESETS = Object.freeze({
  pin_position: [{key: PIN_POSITION_FIELD_KEY, label: 'Pin Position', type: FIELD_TYPE.CHECKBOX, required: false}],
  slug: [{key: SLUG_FIELD_KEY, label: 'Slug', type: FIELD_TYPE.TEXT, required: true}],
  grid_position: [
    {key: gridFieldKey(DEFAULT_LAYOUT_CONTEXT, GRID_FIELD.COLUMN), label: 'Grid column start', type: FIELD_TYPE.NUMBER, required: false},
    {key: gridFieldKey(DEFAULT_LAYOUT_CONTEXT, GRID_FIELD.ROW), label: 'Grid row start', type: FIELD_TYPE.NUMBER, required: false},
    {key: gridFieldKey(DEFAULT_LAYOUT_CONTEXT, GRID_FIELD.WIDTH), label: 'Width', type: FIELD_TYPE.NUMBER, required: false},
    {key: gridFieldKey(DEFAULT_LAYOUT_CONTEXT, GRID_FIELD.HEIGHT), label: 'Height', type: FIELD_TYPE.NUMBER, required: false},
  ],
});

/**
 * Turns a field key or layout context into a readable label: `hero_image` becomes `Hero Image`.
 *
 * @param {string} key
 * @returns {string}
 */
export function humanizeKey(key) {
  return key.replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Detects the field keys a Pebble template reads: every `{{ variable }}` that is not a loop
 * variable or an auto-generated key, plus the collection of every `{% for %}` loop that is not a
 * key the backend injects (such as `page_sections` or `breadcrumbs`).
 *
 * @param {string} content
 * @returns {Set<string>}
 */
export function extractTemplateKeys(content) {
  const forPattern = /{%\s*for\s+(\w+)\s+in\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*(?:\|[^%]*)?\s*%}/g;
  const loopVars = new Set();
  const collectionVars = new Set();

  let forMatch;
  while ((forMatch = forPattern.exec(content)) !== null) {
    loopVars.add(forMatch[1]);
    collectionVars.add(forMatch[2]);
  }

  const varPattern = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*(?:\|[^}]*)?\s*}}/g;
  const foundKeys = new Set();

  let varMatch;
  while ((varMatch = varPattern.exec(content)) !== null) {
    const varName = varMatch[1];
    if (!loopVars.has(varName) && !isAutoGeneratedTemplateFieldKey(varName)) {
      foundKeys.add(varName);
    }
  }

  collectionVars.forEach((variable) => {
    if (!isReservedAutoInjectedTemplateFieldKey(variable)) {
      foundKeys.add(variable);
    }
  });
  return foundKeys;
}

/**
 * @param {string} key trimmed key typed in the manual field input
 * @param {{has: (key: string) => boolean}} existingKeys
 * @returns {string | null} validation message, or null when the key can be added
 */
export function validateManualFieldKey(key, existingKeys) {
  if (!key) {
    return 'Field key is required.';
  }
  if (!FIELD_KEY_PATTERN.test(key)) {
    return 'Use letters, numbers, underscores; start with a letter or underscore.';
  }
  if (isReservedAutoInjectedTemplateFieldKey(key)) {
    return `Field "${key}" is auto-injected by the backend and cannot be added manually.`;
  }
  if (existingKeys.has(key)) {
    return `Field "${key}" already exists.`;
  }
  return null;
}

/**
 * @param {string} key
 * @returns {FieldConfig} a required text field, as assumed for a key detected in the content
 */
export function detectedFieldConfig(key) {
  return {label: humanizeKey(key), type: FIELD_TYPE.TEXT, required: true, manual: false};
}

/**
 * @param {string} key
 * @returns {FieldConfig} an optional field added by hand; the pin position key is a checkbox
 */
export function manualFieldConfig(key) {
  return {
    label: humanizeKey(key),
    type: key === PIN_POSITION_FIELD_KEY ? FIELD_TYPE.CHECKBOX : FIELD_TYPE.TEXT,
    required: false,
    manual: true,
  };
}

/**
 * @param {PresetField} field
 * @returns {FieldConfig}
 */
export function presetFieldConfig(field) {
  return {label: field.label, type: field.type, required: field.required, manual: true};
}

/**
 * Converts a stored template field into editor state. A field counts as manual when the template
 * content does not read it.
 *
 * @param {import('../../shell/types/client.types.js').TemplateFieldType} field
 * @param {Set<string>} detectedKeys
 * @returns {FieldConfig}
 */
export function toFieldConfig(field, detectedKeys) {
  return {
    label: field.label,
    type: field.type,
    required: field.required,
    manual: !detectedKeys.has(field.key),
    editorGroup: field.editorGroup ?? null,
    help: field.help ?? null,
    recommendedLength: field.recommendedLength ?? null,
    dependsOn: field.dependsOn ?? null,
    options: Array.isArray(field.options) ? field.options.join('\n') : '',
    columns: (field.columns ?? []).map(toTableColumnState),
    maxRows: field.maxRows ?? '',
  };
}

/**
 * @param {Map<string, FieldConfig>} fieldConfigs
 * @returns {{key: string, label: string}[]} the checkbox fields, labelled for a select
 */
export function checkboxFieldOptions(fieldConfigs) {
  return [...fieldConfigs.entries()]
    .filter(([, config]) => config.type === FIELD_TYPE.CHECKBOX)
    .map(([key, config]) => ({key, label: config.label || humanizeKey(key)}));
}

/**
 * @param {{key: string}[]} checkboxFields
 * @param {string} value selected checkbox field key
 * @returns {string} the value when it names one of the checkbox fields, otherwise an empty string
 */
export function selectableCheckboxField(checkboxFields, value) {
  return checkboxFields.some((field) => field.key === value) ? value : '';
}
